// ConfigPeek — the contractual-configuration peek, rendered by the global
// PeekHost from `?peek=contract-configs:<id>` (ADR-036 §2). No detail endpoint —
// the list row carries every field (the reference-data property), so the peek
// reads the cached list. The body is THE CHARTER PROJECTED in the role grammar
// the form mirrors (read↔write are mirror images): Applies to → Pricing
// drivers → Contractual commitments, each role section wearing its 16px icon +
// quiet missing count (the absence grammar's aggregate volume). The acts are the
// configuration's ONE registry (`ConfigActions.tsx` — a peek-only entity): Edit
// configuration on top (what is missing is the body's observation cards), Clone ·
// the lifecycle · Delete in the footer's door beside the updated stamp. Write
// acts are key-gated — the peek itself is member-readable (member-vocab reads).

import { useTranslation } from 'react-i18next';
import { configIncompleteFields } from '@ems-app/schemas/invoicing/charter';
import type { ContractualConfig } from '@ems-app/schemas/invoicing/responses';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { ObservationCard } from '../../components/patterns/observations.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { Alert } from '../../components/ui/Alert.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { InfoPopover } from '../../components/ui/InfoPopover.js';
import { icons } from '../../components/ui/icons.js';
import { formatDate } from '../../lib/format.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { useContractConfigs } from '../../hooks/useContractCatalogue.js';
import { pricedSources } from './charterMeta.js';
import { useConfigActions } from './ConfigActions.js';
import {
  useCatalogueContext,
  bandLabel,
  commitmentSections,
  commitmentSlots,
  pricingSourceRows,
  samePricingPeers,
  ConfigStatusBadge,
  configObservations,
  MissingValue,
  SectionTitle,
  GAP_TONE_CLASS,
  SOURCE_ICON,
  type GapTone,
  type Tr,
} from './configDisplay.js';

export interface ConfigPeekProps {
  projectId: string;
  configId: number;
  onClose: () => void;
}

function PeekValue({ children, tone = 'pricing' }: { children: string; tone?: GapTone }) {
  const { t } = useTranslation('invoicing');
  // A '—' reaching the peek is an applicable gap (structural absence never
  // renders a row) — say Missing, coloured by role (GAP_TONE_CLASS): orange
  // for a priced gap, blue for a commitment gap.
  return children === '—' ? (
    <span className={GAP_TONE_CLASS[tone]}>{t('configs.missing')}</span>
  ) : (
    <span dir="ltr">{children}</span>
  );
}

export function ConfigPeek({ projectId, configId, onClose }: ConfigPeekProps) {
  const { t } = useTranslation('invoicing');
  const tr: Tr = t as Tr;
  const { t: tErrors } = useTranslation('errors');
  // The screen's one zone (ADR-082 §2): the project's — a project screen.
  const display = useDisplayZone({ kind: 'project', projectId });
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  const configs = useContractConfigs(projectId);
  const ctx = useCatalogueContext(projectId);
  const cfg = configs.data?.find((c) => c.id === configId);
  const { acts, overlays } = useConfigActions(projectId, cfg, { onDeleted: onClose });

  if (configs.isPending || ctx.isPending) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('configs.peek.kind')}>
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoader />
        </div>
      </ContextPanel>
    );
  }
  if (configs.isError || ctx.isError || !cfg) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('configs.peek.kind')}>
        <Alert variant="danger">{tErrors('CONFIG_NOT_FOUND')}</Alert>
      </ContextPanel>
    );
  }

  const natures = ctx.natures;
  const missing = configIncompleteFields(cfg, natures);
  // The peek's icon strip mirrors the list glyph — the PRICED-address set
  // (the glyph-vs-rollup split); commitment answers live in their section.
  const sources = pricedSources(natures);
  // Configs sharing this one's exact priced address (cheap over tens of rows).
  const pricingPeers = samePricingPeers(configs.data ?? [], natures).get(cfg.id) ?? [];
  const obs = configObservations(cfg, natures, missing, pricingPeers.length + 1);
  const pricing = pricingSourceRows(ctx, natures, cfg, tr);
  const sections = commitmentSections(ctx, natures, cfg, tr);
  const slots = commitmentSlots(sections);
  const commitMissing = slots.total - slots.filled;
  const classEngaged =
    natures.site_type !== 'off' || natures.environment !== 'off' || natures.priority !== 'off' || natures.category !== 'off';

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<icons.contractualConfig size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
        title={cfg.name}
        kind={t('configs.peek.kind')}
        status={<ConfigStatusBadge disabled={cfg.is_disabled} />}
        acts={acts}
        pageless
        footer={
          // The trailing extra beside the door (the registry draws the door at the footer's start).
          <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
            {t('configs.peek.updated', { date: formatDate(cfg.updated_at, 'date', { timeZone: zone }) ?? '—' })}
          </span>
        }
      >
        <div className="flex flex-col gap-3">
          {(sources.length > 0 || cfg.description) && (
            <div className="flex flex-col gap-2">
              {sources.length > 0 && (
                <div className="flex items-center gap-2">
                  {sources.map((s) => {
                    const SrcIcon = SOURCE_ICON[s.key];
                    const on = cfg[s.requiredField as keyof ContractualConfig] === true;
                    return (
                      <SrcIcon
                        key={s.key}
                        size={20}
                        aria-hidden
                        className={on ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-disabled)] opacity-60'}
                      />
                    );
                  })}
                </div>
              )}
              {cfg.description && <p className="text-sm text-[var(--color-text-secondary)]">{cfg.description}</p>}
            </div>
          )}

          {/* THE OBSERVATIONS (ADR-061 §5) — worst first, the same cards every
              register's peek shows. No per-card verb ON PURPOSE: every gap's
              fix is the ONE config form, already the top row's Edit
              configuration — N buttons to one door would be noise; the
              collision has no verb (its detail is the peers line below). */}
          {obs.length > 0 && (
            <DetailSection dense title={t('observations.lens')}>
              <div className="flex flex-col gap-2">
                {obs.map((o, i) => (
                  <ObservationCard
                    key={`${o.family}:${o.subjectKey ?? i}`}
                    kind={o.kind}
                    impact={o.impact}
                    kindLabel={t(`observations.kind.${o.kind}`)}
                    subject={o.subjectKey ? t(o.subjectKey) : t(`observations.family.${o.family}`)}
                  />
                ))}
              </div>
            </DetailSection>
          )}

          {/* Same pricing (info) — one peer names it; several count, their names behind the count (a tap away).
              A shared address means a site here draws multiple recommendations. */}
          {pricingPeers.length > 0 && (
            <p className="inline-flex items-center gap-1.5 text-sm text-[var(--color-info)]">
              <icons.samePricing size={15} aria-hidden />
              {pricingPeers.length === 1 ? (
                t('configs.samePricing.withOne', { name: pricingPeers[0]!.name })
              ) : (
                <InfoPopover label={t('configs.samePricing.withMany', { count: pricingPeers.length })} trigger={t('configs.samePricing.withMany', { count: pricingPeers.length })} triggerClassName="underline decoration-dotted underline-offset-2">
                  <ul className="space-y-0.5">
                    {pricingPeers.map((p) => (
                      <li key={p.id}>{p.name}</li>
                    ))}
                  </ul>
                </InfoPopover>
              )}
            </p>
          )}

          {classEngaged && (
            // Order (his ruling): Category → Environment → Type → Priority.
            <DetailSection title={t('configs.peek.appliesTo')} dense>
              <DescriptionList>
                {natures.category !== 'off' && (
                  <Field label={t('form.categories')}>
                    {cfg.category_ids.length > 0 ? (
                      cfg.category_ids.map((id) => ctx.categories.get(id) ?? id).join(', ')
                    ) : natures.category === 'priced' ? (
                      <MissingValue />
                    ) : (
                      '—'
                    )}
                  </Field>
                )}
                {natures.environment !== 'off' && (
                  <Field label={t('form.environment')}>
                    {cfg.environment ? (
                      t(`form.environments.${cfg.environment}`)
                    ) : natures.environment === 'priced' ? (
                      <MissingValue />
                    ) : (
                      <span className="text-[var(--color-text-disabled)]">—</span>
                    )}
                  </Field>
                )}
                {natures.site_type !== 'off' && (
                  <Field label={t('form.siteTypes')}>
                    {cfg.site_type_ids.length > 0 ? (
                      cfg.site_type_ids.map((id) => ctx.siteTypes.get(id) ?? id).join(', ')
                    ) : natures.site_type === 'priced' ? (
                      <MissingValue />
                    ) : (
                      '—'
                    )}
                  </Field>
                )}
                {natures.priority !== 'off' && (
                  <Field label={t('form.priorities')}>
                    {cfg.priority_ids.length > 0 ? (
                      cfg.priority_ids.map((id) => ctx.priorities.get(id) ?? id).join(', ')
                    ) : natures.priority === 'priced' ? (
                      <MissingValue />
                    ) : (
                      '—'
                    )}
                  </Field>
                )}
              </DescriptionList>
            </DetailSection>
          )}

          <DetailSection title={<SectionTitle>{t('terms.groups.drivers')}</SectionTitle>} dense>
            <DescriptionList>
              <Field label={t('configs.glyph.load')}>
                <PeekValue>{bandLabel(ctx, 'load', cfg.load_band_id)}</PeekValue>
              </Field>
              {pricing.map((r) => (
                <Field key={r.label} label={r.label}>
                  {r.missing ? <MissingValue /> : <PeekValue>{r.value}</PeekValue>}
                </Field>
              ))}
            </DescriptionList>
          </DetailSection>

          {sections.length > 0 && (
            <DetailSection
              title={
                <SectionTitle missing={commitMissing} tone="commitment">
                  {t('terms.groups.commitments')}
                </SectionTitle>
              }
              dense
            >
              {/* The Terms page's cluster grammar, read side: the source named
                  once (its icon; a commitment-nature presence answers on the
                  heading), short term labels indented beneath. */}
              <div className="flex flex-col gap-2.5">
                {sections.map((s) => {
                  const ClusterIcon = s.icon;
                  return (
                    <div key={s.key}>
                      <p className="flex items-center justify-between gap-3 text-sm">
                        <span className="inline-flex min-w-0 items-center gap-2 font-medium text-[var(--color-text-primary)]">
                          <ClusterIcon size={15} aria-hidden className="shrink-0 text-[var(--color-text-secondary)]" />
                          <span className="truncate">{s.name}</span>
                        </span>
                        {s.presence !== undefined &&
                          (s.presence === '—' ? (
                            <MissingValue tone="commitment" />
                          ) : (
                            <span className="shrink-0 text-[var(--color-text-secondary)]">{s.presence}</span>
                          ))}
                      </p>
                      {s.rows.length > 0 && (
                        <div className="ps-6">
                          <DescriptionList>
                            {s.rows.map((r) => (
                              <Field key={r.label} label={r.label}>
                                <PeekValue tone="commitment">{r.value}</PeekValue>
                              </Field>
                            ))}
                          </DescriptionList>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </DetailSection>
          )}
        </div>
      </ContextPanel>

      {overlays}
    </>
  );
}
